import type { SeatCandidate } from "@anjabom/core";
import { useEffect, useId, useState } from "react";
import { seatKey } from "../lib/contribution-client";
import type { PublicTip } from "./TipList";

export async function loadSeatTips(key: string, signal: AbortSignal): Promise<PublicTip[]> {
	const rowKey = key.slice(0, key.lastIndexOf(":"));
	const response = await fetch(`/api/rows/${encodeURIComponent(rowKey)}/tips`, { signal });
	if (!response.ok) throw new Error("tips unavailable");
	const data = await response.json() as { tips: PublicTip[] };
	if (!Array.isArray(data.tips)) throw new Error("invalid tips");
	return data.tips.filter((tip) => tip.seatKey === key);
}

/** Facility-seat experience stays distinct from the current show's configuration. */
export function SeatPublicTips({ candidate, limit, refreshKey = 0 }: {
	candidate: SeatCandidate;
	limit?: number;
	refreshKey?: number;
}) {
	const heading = useId();
	const [tips, setTips] = useState<PublicTip[] | null>(null);
	const [error, setError] = useState(false);
	const [attempt, setAttempt] = useState(0);
	const key = seatKey(candidate);
	// biome-ignore lint/correctness/useExhaustiveDependencies: explicit retry and publish refresh re-read the same seat.
	useEffect(() => {
		const controller = new AbortController();
		setTips(null); setError(false);
		void loadSeatTips(key, controller.signal).then((value) => {
			if (!controller.signal.aborted) setTips(value);
		}).catch(() => { if (!controller.signal.aborted) setError(true); });
		return () => controller.abort();
	}, [key, attempt, refreshKey]);

	return <section className="seat-preview-people" aria-labelledby={heading}>
		<h3 id={heading}>앉아본 사람 한 줄</h3>
		{error ? <div><p role="alert">한 줄을 불러오지 못했어요. 기록이 없는지는 아직 몰라요.</p><button type="button" className="button" onClick={() => setAttempt((value) => value + 1)}>한 줄 다시 불러오기</button></div>
			: tips === null ? <p role="status">이 자리에 남긴 한 줄을 확인하고 있어요.</p>
				: tips.length ? <><p className="micro">관람 당시의 경험이에요. 이번 공연의 무대·시야와 다를 수 있어요.</p>{tips.slice(0, limit).map((tip) => <article className="seat-preview-tip" key={tip.id}><p className="micro">{tip.observedMonth} · {tip.source === "anonymous" ? "로그인 없이 남긴 경험" : tip.verification === "purchase" ? "구매 인증" : "앉아본 기록"}</p><p>{tip.note}</p>{tip.tags.length ? <p className="micro">{tip.tags.join(" · ")}</p> : null}</article>)}{limit && tips.length > limit ? <p className="micro">다른 관람 기록은 자세히 보기에서 더 살펴봐요.</p> : null}</>
					: <p>이 자리에는 아직 한 줄이 없어요. 공식 위치부터 확인해 보세요.</p>}
	</section>;
}
